import { useEffect, useMemo, useState } from 'react';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { FormField, FormFieldType } from 'shared/modals/ApplicationEditor/constants';
import { LangObjectArray } from '@brg/api/dist/api-core/utils/lang-utils';

const checkVideoLinks = (s:string) => {
  const youbeReg = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
  const vimeoReg = /^(http|https)?:\/\/?(vimeo.com)/;
  const vkReg = /^(https:\/\/)?(vk.com)/;
  const vkVideoReg = /^(https:\/\/)?(vkvideo.ru)/;  
  const kinescopeReg = /kinescope\.io/i;

  return youbeReg.test(s) || vimeoReg.test(s) || vkReg.test(s) || vkVideoReg.test(s) || kinescopeReg.test(s);
}

const checkCloudLinks = (s: string): boolean => {
  if (!s) return false;

  const lower = s.trim().toLowerCase();
  const dropbox = /(dropboxusercontent|dropbox)\.com\/(scl\/(fi|fo)|s\/|sh\/|home\/|u\/)/;
  const googleDrive = /(drive|docs|spreadsheets|presentation)\.google\.com\/(drive\/folders|file\/d|open|document\/d|spreadsheets\/d|presentation\/d|spreadsheets\/d\/edit|document\/d\/edit|presentation\/d\/edit)/;
  const yandexDisk = /(disk\.(yandex\.ru|yandex\.com|360\.yandex\.ru)|yadi\.sk)\/(d|i\/|public|client)/;

  return (
    dropbox.test(lower) ||
    googleDrive.test(lower) ||
    yandexDisk.test(lower)
  );
};

const checkUrl = (s: string): boolean => {
  return /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/?.test(s)
};

/** @username, username или ссылка t.me / https://… */
const checkTelegramValue = (value: string): boolean => {
  const trimmed = value.trim();
  if (!trimmed) return false;

  if (/^https?:\/\//i.test(trimmed)) {
    return checkUrl(trimmed);
  }

  if (/^(t\.me|telegram\.me)\//i.test(trimmed)) {
    return checkUrl(`https://${trimmed}`);
  }

  const handle = trimmed.startsWith('@') ? trimmed.slice(1) : trimmed;
  return /^[a-zA-Z0-9_]{2,32}$/.test(handle);
};

const PHONE_MIN_DIGITS = 10;
const PHONE_MAX_DIGITS = 15;

const getPhoneDigits = (value?: string | null): string =>
  (value ?? '').replace(/\D/g, '');

/** Полный номер: 10 цифр национальной части или 11 с ведущей 7/8; без «обрыва» на разделителе. */
const isCompletePhoneNumber = (value?: string | null): boolean => {
  const raw = (value ?? '').trim();
  if (!raw) return false;

  if (/[-.\s(]$/.test(raw)) return false;

  const digits = getPhoneDigits(raw);
  if (digits.length < PHONE_MIN_DIGITS || digits.length > PHONE_MAX_DIGITS) {
    return false;
  }

  if (digits.startsWith('7') || digits.startsWith('8')) {
    return digits.length === 11;
  }

  return digits.length === PHONE_MIN_DIGITS;
};

const getPhoneValidation = (field: FormField, t: (k: string, p?: any) => string) => {
  const phoneMsg = t('competition_edit.form.message.phone');
  const requiredMsg = t('competition_edit.form.message.required.required_field');

  let schema = yup.string();

  if (field.isRequired) {
    schema = schema.required(requiredMsg);
  }

  return schema.test('phone-complete', phoneMsg, (value) => {
    const raw = (value ?? '').trim();
    const digits = getPhoneDigits(raw);

    if (!raw || !digits || raw === '+7' || digits === '7') {
      return !field.isRequired;
    }

    return isCompletePhoneNumber(value);
  });
};

const hasUploadedFileValue = (value: unknown): boolean => {
  if (value === true) return true;
  if (value == null) return false;
  if (typeof FileList !== 'undefined' && value instanceof FileList) return value.length > 0;
  if (typeof File !== 'undefined' && value instanceof File) return true;
  if (typeof value === 'object' && value !== null && 'length' in value) {
    const n = Number((value as { length: number }).length);
    return Number.isFinite(n) && n > 0;
  }
  const files = (value as { target?: { files?: FileList } })?.target?.files;
  return !!files?.length;
};

const getFileUploadValidation = (field: FormField, t: (k: string, p?: any) => string) => {
  const msg = t('competition_edit.form.message.required.required_field');
  if (!field.isRequired) {
    return yup.mixed().nullable().optional();
  }
  return yup
    .mixed()
    .required(msg)
    .test('file-present', msg, (value) => hasUploadedFileValue(value));
};

const isLegalDocumentAgreed = (value: unknown): boolean =>
  value === true || value === 1;

const getLegalDocumentValidation = (field: FormField, t: (k: string, p?: any) => string) => {
  const msg = t('competition_edit.form.message.required.agree');
  if (!field.isRequired) {
    return yup.mixed().optional();
  }
  return yup
    .mixed()
    .test('legal-document-agreed', msg, (value) => isLegalDocumentAgreed(value));
};

export interface FieldConfig {
    type: any;
    label: string;
    placeholder?: string;
    hidden?: boolean;
    name?: string;
    requiredLabel?: boolean;
    bilingual?: boolean;
    checkboxLabel?: string;
    defaultValue?: any;
    options?: any[];
    validation: yup.AnySchema;
    isOldLegalCheckbox?: boolean;
  }

const emptyLangPlaceholder = [] as unknown as LangObjectArray;

const buildOldCheckboxFieldConfigs = (
  oldCheckboxes: LangObjectArray[] | undefined,
  t: (k: string, p?: any) => string
) => {
  if (!oldCheckboxes?.length) return [];

  const requiredMessage = t('competition_edit.form.message.required.required_field');

  return oldCheckboxes.map((headerLabels) => ({
    type: FormFieldType.Text,
    header: headerLabels,
    placeholder: emptyLangPlaceholder,
    isRequired: true,
    inlineVariants: [],
    fieldIdInCrm: 0,
    isTagInCrm: false,
    isDisabled: false,
    label: headerLabels as any,
    requiredLabel: true,
    validation: yup.bool().oneOf([true], requiredMessage),
    isOldLegalCheckbox: true,
  })) as unknown as Array<FormField & FieldConfig & { name: string }>;
};

const requiredFieldMsg = (t: (k: string, p?: any) => string) =>
  t('competition_edit.form.message.required.required_field');

const getStringFieldValidation = (
  field: FormField,
  t: (k: string, p?: any) => string,
  maxLength = 800
) => {
  const msg = requiredFieldMsg(t);
  const noSpacesOnlyMsg = t('competition_edit.form.message.no_space_symbol');

  let schema = yup
    .string()
    .max(maxLength, t('competition_edit.form.message.max', { count: maxLength }))
    .test('no-spaces-only', noSpacesOnlyMsg, (value) => {
      if (value == null || value === '') return true;
      return value.trim().length > 0;
    });

  if (field.isRequired) {
    schema = schema.required(msg);
  } else {
    schema = schema.optional();
  }

  return schema;
};

const getEmailFieldValidation = (field: FormField, t: (k: string, p?: any) => string) => {
  const emailMsg = t('competition_edit.form.message.email');
  const requiredMsg = t('competition_edit.form.message.required.email');

  if (field.isRequired) {
    return yup.string().required(requiredMsg).email(emailMsg);
  }

  return yup
    .string()
    .optional()
    .test('email-if-filled', emailMsg, (value) => !value?.trim() || yup.string().email().isValidSync(value));
};

const getDateFieldValidation = (field: FormField, t: (k: string, p?: any) => string) => {
  const msg = requiredFieldMsg(t);
  const notEmptyMsg = t('competition_edit.form.message.required.not_empty');

  let schema = yup.date().nullable().typeError(notEmptyMsg);

  if (field.isRequired) {
    schema = schema.required(msg);
  } else {
    schema = schema.optional();
  }

  return schema;
};

const getValidationByType = (field: FormField, t: (k: string, p?: any) => string) => {
  const msg = requiredFieldMsg(t);

  switch (field.type) {
    case FormFieldType.Text:
    case FormFieldType.Name:
    case FormFieldType.Surname:
    case FormFieldType.Comment:
      return getStringFieldValidation(field, t);
    case FormFieldType.Mail:
    case FormFieldType.AdditionalMail:
      return getEmailFieldValidation(field, t);
    case FormFieldType.Phone:
      return getPhoneValidation(field, t);
    case FormFieldType.SelectOne:
      if (!field.isRequired) {
        return yup.mixed().optional();
      }
      return yup.mixed().required(msg);
    case FormFieldType.File:
      return getFileUploadValidation(field, t);
    case FormFieldType.LegalDocument:
      return getLegalDocumentValidation(field, t);
    case FormFieldType.Education:
      return yup.string().required(requiredFieldMsg(t));
    case FormFieldType.SelectMultiple:
      if (!field.isRequired) {
        return yup.array().of(yup.mixed()).optional();
      }
      return yup
        .array()
        .of(yup.mixed())
        .min(1, msg)
        .required(msg);
    case FormFieldType.Telegram: {
      const urlMsg = t('competition_edit.form.message.url');
      let schema = yup.string().test(
        'is-telegram',
        urlMsg,
        (value) => !value?.trim() || checkTelegramValue(value),
      );

      if (field.isRequired) {
        schema = schema.required(msg);
      } else {
        schema = schema.optional();
      }

      return schema;
    }
    case FormFieldType.Date:
      return getDateFieldValidation(field, t);
    default:
      return yup.mixed().optional();
  }
};


export const useGetFieldConfig = (
  fields?: FormField[],
  oldCheckboxes?: LangObjectArray[],
  projectId?: string,
  nominationId?: string
) => {
    const [configLoading, setConfigLoading] = useState(true)
    const { t } = useTranslation();

    useEffect(() => {
      if (projectId && nominationId && fields && fields.length > 0) {
          setConfigLoading(false)
      }
    } , [projectId, nominationId, fields])

    return useMemo(() => {
        const mainFields =
          fields?.filter?.((field) => !field.isDisabled).map((field) => ({
            ...field,
            label: field.header,
            placeholder: field.placeholder,
            validation: getValidationByType(field, t),
          })) ?? [];

        return {
          config: [...mainFields, ...buildOldCheckboxFieldConfigs(oldCheckboxes, t)],
          loading: configLoading,
        };
      }, [fields, oldCheckboxes, t, configLoading]);

}import { useEffect, useMemo, useState } from 'react';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { FormField, FormFieldType } from 'shared/modals/ApplicationEditor/constants';
import { LangObjectArray } from '@brg/api/dist/api-core/utils/lang-utils';

const checkVideoLinks = (s:string) => {
  const youbeReg = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
  const vimeoReg = /^(http|https)?:\/\/?(vimeo.com)/;
  const vkReg = /^(https:\/\/)?(vk.com)/;
  const vkVideoReg = /^(https:\/\/)?(vkvideo.ru)/;  
  const kinescopeReg = /kinescope\.io/i;

  return youbeReg.test(s) || vimeoReg.test(s) || vkReg.test(s) || vkVideoReg.test(s) || kinescopeReg.test(s);
}

const checkCloudLinks = (s: string): boolean => {
  if (!s) return false;

  const lower = s.trim().toLowerCase();
  const dropbox = /(dropboxusercontent|dropbox)\.com\/(scl\/(fi|fo)|s\/|sh\/|home\/|u\/)/;
  const googleDrive = /(drive|docs|spreadsheets|presentation)\.google\.com\/(drive\/folders|file\/d|open|document\/d|spreadsheets\/d|presentation\/d|spreadsheets\/d\/edit|document\/d\/edit|presentation\/d\/edit)/;
  const yandexDisk = /(disk\.(yandex\.ru|yandex\.com|360\.yandex\.ru)|yadi\.sk)\/(d|i\/|public|client)/;

  return (
    dropbox.test(lower) ||
    googleDrive.test(lower) ||
    yandexDisk.test(lower)
  );
};

const checkUrl = (s: string): boolean => {
  return /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/?.test(s)
};

/** @username, username или ссылка t.me / https://… */
const checkTelegramValue = (value: string): boolean => {
  const trimmed = value.trim();
  if (!trimmed) return false;

  if (/^https?:\/\//i.test(trimmed)) {
    return checkUrl(trimmed);
  }

  if (/^(t\.me|telegram\.me)\//i.test(trimmed)) {
    return checkUrl(`https://${trimmed}`);
  }

  const handle = trimmed.startsWith('@') ? trimmed.slice(1) : trimmed;
  return /^[a-zA-Z0-9_]{2,32}$/.test(handle);
};

const PHONE_MIN_DIGITS = 10;
const PHONE_MAX_DIGITS = 15;

const getPhoneDigits = (value?: string | null): string =>
  (value ?? '').replace(/\D/g, '');

/** Полный номер: 10 цифр национальной части или 11 с ведущей 7/8; без «обрыва» на разделителе. */
const isCompletePhoneNumber = (value?: string | null): boolean => {
  const raw = (value ?? '').trim();
  if (!raw) return false;

  if (/[-.\s(]$/.test(raw)) return false;

  const digits = getPhoneDigits(raw);
  if (digits.length < PHONE_MIN_DIGITS || digits.length > PHONE_MAX_DIGITS) {
    return false;
  }

  if (digits.startsWith('7') || digits.startsWith('8')) {
    return digits.length === 11;
  }

  return digits.length === PHONE_MIN_DIGITS;
};

const getPhoneValidation = (field: FormField, t: (k: string, p?: any) => string) => {
  const phoneMsg = t('competition_edit.form.message.phone');
  const requiredMsg = t('competition_edit.form.message.required.required_field');

  let schema = yup.string();

  if (field.isRequired) {
    schema = schema.required(requiredMsg);
  }

  return schema.test('phone-complete', phoneMsg, (value) => {
    const raw = (value ?? '').trim();
    const digits = getPhoneDigits(raw);

    if (!raw || !digits || raw === '+7' || digits === '7') {
      return !field.isRequired;
    }

    return isCompletePhoneNumber(value);
  });
};

const hasUploadedFileValue = (value: unknown): boolean => {
  if (value === true) return true;
  if (value == null) return false;
  if (typeof FileList !== 'undefined' && value instanceof FileList) return value.length > 0;
  if (typeof File !== 'undefined' && value instanceof File) return true;
  if (typeof value === 'object' && value !== null && 'length' in value) {
    const n = Number((value as { length: number }).length);
    return Number.isFinite(n) && n > 0;
  }
  const files = (value as { target?: { files?: FileList } })?.target?.files;
  return !!files?.length;
};

const getFileUploadValidation = (field: FormField, t: (k: string, p?: any) => string) => {
  const msg = t('competition_edit.form.message.required.required_field');
  if (!field.isRequired) {
    return yup.mixed().nullable().optional();
  }
  return yup
    .mixed()
    .required(msg)
    .test('file-present', msg, (value) => hasUploadedFileValue(value));
};

const isLegalDocumentAgreed = (value: unknown): boolean =>
  value === true || value === 1;

const getLegalDocumentValidation = (field: FormField, t: (k: string, p?: any) => string) => {
  const msg = t('competition_edit.form.message.required.agree');
  if (!field.isRequired) {
    return yup.mixed().optional();
  }
  return yup
    .mixed()
    .test('legal-document-agreed', msg, (value) => isLegalDocumentAgreed(value));
};

export interface FieldConfig {
    type: any;
    label: string;
    placeholder?: string;
    hidden?: boolean;
    name?: string;
    requiredLabel?: boolean;
    bilingual?: boolean;
    checkboxLabel?: string;
    defaultValue?: any;
    options?: any[];
    validation: yup.AnySchema;
    isOldLegalCheckbox?: boolean;
  }

const emptyLangPlaceholder = [] as unknown as LangObjectArray;

const buildOldCheckboxFieldConfigs = (
  oldCheckboxes: LangObjectArray[] | undefined,
  t: (k: string, p?: any) => string
) => {
  if (!oldCheckboxes?.length) return [];

  const requiredMessage = t('competition_edit.form.message.required.required_field');

  return oldCheckboxes.map((headerLabels) => ({
    type: FormFieldType.Text,
    header: headerLabels,
    placeholder: emptyLangPlaceholder,
    isRequired: true,
    inlineVariants: [],
    fieldIdInCrm: 0,
    isTagInCrm: false,
    isDisabled: false,
    label: headerLabels as any,
    requiredLabel: true,
    validation: yup.bool().oneOf([true], requiredMessage),
    isOldLegalCheckbox: true,
  })) as unknown as Array<FormField & FieldConfig & { name: string }>;
};

const requiredFieldMsg = (t: (k: string, p?: any) => string) =>
  t('competition_edit.form.message.required.required_field');

const getStringFieldValidation = (
  field: FormField,
  t: (k: string, p?: any) => string,
  maxLength = 800
) => {
  const msg = requiredFieldMsg(t);
  const noSpacesOnlyMsg = t('competition_edit.form.message.no_space_symbol');

  let schema = yup
    .string()
    .max(maxLength, t('competition_edit.form.message.max', { count: maxLength }))
    .test('no-spaces-only', noSpacesOnlyMsg, (value) => {
      if (value == null || value === '') return true;
      return value.trim().length > 0;
    });

  if (field.isRequired) {
    schema = schema.required(msg);
  } else {
    schema = schema.optional();
  }

  return schema;
};

const getEmailFieldValidation = (field: FormField, t: (k: string, p?: any) => string) => {
  const emailMsg = t('competition_edit.form.message.email');
  const requiredMsg = t('competition_edit.form.message.required.email');

  if (field.isRequired) {
    return yup.string().required(requiredMsg).email(emailMsg);
  }

  return yup
    .string()
    .optional()
    .test('email-if-filled', emailMsg, (value) => !value?.trim() || yup.string().email().isValidSync(value));
};

const getDateFieldValidation = (field: FormField, t: (k: string, p?: any) => string) => {
  const msg = requiredFieldMsg(t);
  const notEmptyMsg = t('competition_edit.form.message.required.not_empty');

  let schema = yup.date().nullable().typeError(notEmptyMsg);

  if (field.isRequired) {
    schema = schema.required(msg);
  } else {
    schema = schema.optional();
  }

  return schema;
};

const getValidationByType = (field: FormField, t: (k: string, p?: any) => string) => {
  const msg = requiredFieldMsg(t);

  switch (field.type) {
    case FormFieldType.Text:
    case FormFieldType.Name:
    case FormFieldType.Surname:
    case FormFieldType.Comment:
      return getStringFieldValidation(field, t);
    case FormFieldType.Mail:
    case FormFieldType.AdditionalMail:
      return getEmailFieldValidation(field, t);
    case FormFieldType.Phone:
      return getPhoneValidation(field, t);
    case FormFieldType.SelectOne:
      if (!field.isRequired) {
        return yup.mixed().optional();
      }
      return yup.mixed().required(msg);
    case FormFieldType.File:
      return getFileUploadValidation(field, t);
    case FormFieldType.LegalDocument:
      return getLegalDocumentValidation(field, t);
    case FormFieldType.Education:
      return yup.string().required(requiredFieldMsg(t));
    case FormFieldType.SelectMultiple:
      if (!field.isRequired) {
        return yup.array().of(yup.mixed()).optional();
      }
      return yup
        .array()
        .of(yup.mixed())
        .min(1, msg)
        .required(msg);
    case FormFieldType.Telegram: {
      const urlMsg = t('competition_edit.form.message.url');
      let schema = yup.string().test(
        'is-telegram',
        urlMsg,
        (value) => !value?.trim() || checkTelegramValue(value),
      );

      if (field.isRequired) {
        schema = schema.required(msg);
      } else {
        schema = schema.optional();
      }

      return schema;
    }
    case FormFieldType.Date:
      return getDateFieldValidation(field, t);
    default:
      return yup.mixed().optional();
  }
};


export const useGetFieldConfig = (
  fields?: FormField[],
  oldCheckboxes?: LangObjectArray[],
  projectId?: string,
  nominationId?: string
) => {
    const [configLoading, setConfigLoading] = useState(true)
    const { t } = useTranslation();

    useEffect(() => {
      if (projectId && nominationId && fields && fields.length > 0) {
          setConfigLoading(false)
      }
    } , [projectId, nominationId, fields])

    return useMemo(() => {
        const mainFields =
          fields?.filter?.((field) => !field.isDisabled).map((field) => ({
            ...field,
            label: field.header,
            placeholder: field.placeholder,
            validation: getValidationByType(field, t),
          })) ?? [];

        return {
          config: [...mainFields, ...buildOldCheckboxFieldConfigs(oldCheckboxes, t)],
          loading: configLoading,
        };
      }, [fields, oldCheckboxes, t, configLoading]);

}